import { createSelector } from 'reselect'
import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import { sum } from 'lib/array/arrayUtils'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'

const getCheckoutCartBedbankRoomRateIds = (items: Array<App.Checkout.AnyItem>) => items.filter(isBedbankItem).map(item => item.roomRateId)

/**
 * Check if the cart has a property collect item
 * @param state - The state of the checkout cart
 * @returns true if the cart has a property collect item, false otherwise
 */
export const getCartHasPropertyCollectItem = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (items, sessions) => {
    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)
    // it is required to cross check rateIds from checkout cart items as the session data sometime contain more item than added to checkout cart items
    return Object.values(sessions)
      .some(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))
  })

export const getPayAtPropertyAmount = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (items, sessions) => {
    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)

    const payAtPropertySessionItems = Object.values(sessions)
      .filter(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))

    // There can be only one pay at property item in the cart
    return sum(payAtPropertySessionItems.map(session => session.data?.totals?.inclusive || 0))
  })

/**
 * Get the pay at property details, Whether cart has at least one property collect item and if true then the pay at property totals
 * @param state - The state of the checkout cart
 * @returns the pay at property details if cart has at least one property collect item, otherwise { cartHasPropertyCollectItem: false }
 * payAtPropertyAmount - the pay at property amount in the current region currency
 * payAtPropertyAmountInPropertyCurrency - the pay at property amount in the property currency
 * checkInDate - the check in date in the format of DD MMM YYYY
 */
export const getPayAtPropertyDetails = createSelector(
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.checkout.cart.currencyCode,
  (cartHasPropertyCollectItem, items, sessions, currentCurrency, bedbankOffers, checkoutCurrency): {
    cartHasPropertyCollectItem: false;
  } | {
    cartHasPropertyCollectItem: true;
    payAtPropertyAmount: number;
    payAtPropertyAmountInPropertyCurrency: string;
    payAtPropertyCurrencyCode: string;
    checkInDate: string;
  } => {
    if (!cartHasPropertyCollectItem) {
      return {
        cartHasPropertyCollectItem: false,
      }
    }

    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)

    const payAtPropertySessionItems = Object.values(sessions)
      .filter(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))

    // There can be only one pay at property item in the cart
    const payAtPropertySessionItem = payAtPropertySessionItems[0]

    const checkoutCartItem = items.find(item => item.itemType === 'bedbankHotel' && item.roomRateId === payAtPropertySessionItem?.data?.roomRateId)

    const offer = checkoutCartItem?.itemType === 'bedbankHotel' ? bedbankOffers[checkoutCartItem.offerId] : undefined

    const payAtPropertyAmountInPropertyCurrency = formatCurrency(payAtPropertySessionItem?.data?.payAtPropertyTotals?.inclusive ?? 0,
      payAtPropertySessionItem?.data?.payAtPropertyTotals?.currencyCode ?? currentCurrency,
      offer?.property.address.countryCode ?? 'AU')

    return {
      cartHasPropertyCollectItem: true,
      payAtPropertyAmount: payAtPropertySessionItem?.data?.totals?.inclusive || 0, // in current region currency
      checkInDate: payAtPropertySessionItem?.data?.checkIn!,
      payAtPropertyAmountInPropertyCurrency,
      payAtPropertyCurrencyCode: payAtPropertySessionItem?.data?.payAtPropertyTotals?.currencyCode ?? checkoutCurrency ?? currentCurrency,
    }
  })
